






















import { defineComponent, computed, PropType } from '@vue/composition-api';
import FontAwesomeIcon from '../../icons/icons';
import {
  IconButtonSizes,
  IconButtonThemes,
  IconButtonMode,
  IconButtonProps,
  IconButtonIcon,
  ICON_BUTTON_ICONS,
} from './types';

export default defineComponent({
  name: 'IconButton',
  components: {
    FontAwesomeIcon,
  },
  props: {
    id: {
      type: String,
      required: false,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    pressed: {
      type: Boolean,
      default: false,
      required: false,
    },
    mode: {
      type: String as PropType<IconButtonMode>,
      default: 'light',
      required: false,
    },
    buttonTheme: {
      type: String as PropType<IconButtonThemes>,
      required: false,
      default: 'primary',
    },
    customEventName: {
      type: String,
      required: true,
    },
    buttonSize: {
      type: String as PropType<IconButtonSizes>,
      required: false,
      default: 'medium',
    },
    ariaLabel: {
      type: String,
      required: true,
    },
    icon: {
      type: String as PropType<IconButtonIcon>,
      required: true,
    },
    iconRotation: {
      type: Number,
      required: false,
      default: undefined,
    },
  },
  setup(props: IconButtonProps, context: any) {
    const isDisabled = computed(() => props.disabled || props.loading);

    const iconConfig = computed(() => ICON_BUTTON_ICONS[props.icon]);
    const iconName = computed(() => iconConfig.value.icon);
    const iconRotationAngle = computed(() => (
      props.iconRotation !== undefined
        ? props.iconRotation
        : iconConfig.value.rotation
    ));

    const iconRotationStyle = computed(() => {
      const rotation = iconRotationAngle.value;
      if (rotation !== undefined) {
        return { transform: `rotate(${rotation}deg)` };
      }
      return {};
    });

    const sizeClass = computed(() => {
      const sizes = {
        'x-small': 'icon-button--x-small',
        small: 'icon-button--small',
        medium: 'icon-button--medium',
        large: 'icon-button--large',
      };
      return sizes[props.buttonSize as IconButtonSizes];
    });

    const themeClass = computed(() => {
      const classes = ['icon-button-layout'];

      // Add theme
      classes.push(`icon-button--${props.buttonTheme}`);

      // Add mode modifier if dark mode is specified
      if (props.mode === 'dark') {
        classes.push(`icon-button--${props.buttonTheme}--on-dark`);
      }

      // Add mode modifier if dark-white mode is specified
      if (props.mode === 'dark-white') {
        classes.push(`icon-button--${props.buttonTheme}--on-dark-white`);
      }

      // Add pressed modifier to the theme if pressed
      if (props.pressed && !isDisabled.value) {
        classes.push(`icon-button--${props.buttonTheme}--pressed`);
      }

      // Add disabled modifier to the theme if disabled
      if (isDisabled.value) {
        classes.push(`icon-button--${props.buttonTheme}--disabled`, 'icon-button--disabled');
      }

      return classes.join(' ');
    });

    return {
      emitCustomEvent() {
        context.emit(props.customEventName);
      },
      isDisabled,
      getContainerClass: computed(() => 'fc-inline-block'),
      sizeClass,
      themeClass,
      iconName,
      iconRotationAngle,
      iconRotationStyle,
    };
  },
});
